import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import { SCREEN_SIZE } from '@nxt-insurance/ui-kit/constants'
import type { ScreenSize } from '@nxt-insurance/ui-kit/constants'

import type { HorizontalAlignment } from '@/cms/helperTypes'
import { BlockContent } from '@/components/renderers/BlockContent'
import { getLocaleParts } from '@/utils/locale'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const restrictedContent = z.object({
  _type: z.literal('restrictedContent'),
  contentVersions: z
    .array(
      z.object({
        _key: z.string(),
        availableMarkets: z.array(z.string()),
        breakpoint: z.enum(Object.keys(SCREEN_SIZE) as [ScreenSize, ...ScreenSize[]]),
        displayOptions: z.enum(['smaller', 'larger']).optional(),
        content: z.array(z.unknown()),
      })
    )
    .optional(),
})

const restrictedContentStyles = tv({
  base: '',
  variants: {
    breakpoint: {
      S: '',
      M: '',
      L: '',
      XL: '',
      XXL: '',
    },
    displayOption: {
      smaller: '',
      larger: '',
    },
  },
  compoundVariants: [
    {
      breakpoint: 'S',
      displayOption: 'smaller',
      className: 'max-sm:block sm:hidden',
    },
    {
      breakpoint: 'S',
      displayOption: 'larger',
      className: 'max-sm:hidden sm:block',
    },
    {
      breakpoint: 'M',
      displayOption: 'smaller',
      className: 'max-md:block md:hidden',
    },
    {
      breakpoint: 'M',
      displayOption: 'larger',
      className: 'max-md:hidden md:block',
    },
    {
      breakpoint: 'L',
      displayOption: 'smaller',
      className: 'max-lg:block lg:hidden',
    },
    {
      breakpoint: 'L',
      displayOption: 'larger',
      className: 'max-lg:hidden lg:block',
    },
    {
      breakpoint: 'XL',
      displayOption: 'smaller',
      className: 'max-xl:block xl:hidden',
    },
    {
      breakpoint: 'XL',
      displayOption: 'larger',
      className: 'max-xl:hidden xl:block',
    },
    {
      breakpoint: 'XXL',
      displayOption: 'smaller',
      className: 'max-xxl:block xxl:hidden',
    },
    {
      breakpoint: 'XXL',
      displayOption: 'larger',
      className: 'max-xxl:hidden xxl:block',
    },
  ],
})

export const RestrictedContent = (props: {
  node: PortableTextTypeComponentProps<unknown>
  width: number
  sectionName: string
  align?: HorizontalAlignment
}) => {
  const value = useMemo(() => restrictedContent.parse(props.node.value), [props.node.value])
  const { locale } = useRouter()
  const [_lang, market] = getLocaleParts(locale)
  return (
    <div className="flex w-full flex-col">
      {value.contentVersions
        ?.filter((version) => version.availableMarkets.includes(market))
        .map((version) => (
          <div
            className={restrictedContentStyles({
              breakpoint: version.breakpoint,
              displayOption: version.displayOptions,
            })}
            data-testid={`restricted-content-${version.breakpoint}-${JSON.stringify(
              version.displayOptions
            )}`}
            key={version._key}
          >
            <BlockContent
              blocks={version.content}
              width={props.width}
              sectionName={props.sectionName}
              align={props.align}
            />
          </div>
        )) ?? null}
    </div>
  )
}
